import { useEffect, useRef } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useAgentCatalog } from '@/app/providers/AgentCatalogProvider';
import { useChatViewStore } from './viewStore';
import { useChatWorkspaceController } from './controller';

/** Chat lives at "/" and "/agents/*"; settings/schedules render other paths. */
function isChatPath(pathname: string): boolean {
  return pathname === '/' || pathname.startsWith('/agents/');
}

/**
 * Deep-linkable chat URLs (`/agents/:agentId/sessions/:sessionId`).
 *
 * The view store stays the source of truth for the current selection — it is
 * the natural owner of per-agent thread state (the chat runtime creates threads
 * mid-stream, and selection is persisted + synced cross-tab). This hook is the
 * thin bridge to the address bar, in two one-directional pieces that cannot form
 * a loop:
 *
 *   - store → URL (mirror): reflects the selection into the URL. The only writer
 *     of chat URLs. Never navigates on a null selection (that would clobber a
 *     deep link before it is applied) or off a non-chat route (settings); the
 *     back/logo handlers own clearing to "/". Also mirrors a thread that has
 *     been accepted for this tab's turn but whose selection is deliberately
 *     deferred until the run finishes (see ChatWorkspace.handleThreadAccepted),
 *     so a reload mid-stream lands on a resolvable session URL instead of a
 *     bare agent URL — otherwise indistinguishable from an explicit new-chat
 *     intent by the seed effect below.
 *   - URL → store (seed): applies an incoming deep link to the store exactly
 *     once, after ChatSessionProvider has hydrated it (sessionEpoch > 0) —
 *     applying earlier is overwritten by that hydration. Reads the *initial*
 *     route so the mirror cannot shift it first. Browser back/forward is not
 *     reconciled (a known limitation). A bare agent URL (no session segment)
 *     is an explicit new-chat intent for this tab: it clears the agent's
 *     hydrated thread in this tab's view store instead of letting the mirror
 *     rewrite the URL to whatever thread the workspace last had active, which
 *     silently appended fresh prompts to an old conversation. The shared
 *     workspace snapshot is untouched, so other tabs keep their thread.
 *
 * Disabled on the mobile shell, which owns its own navigation stack.
 */
export function useChatUrlSync(enabled: boolean): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // useMatch (not useParams) so this resolves even though the hook runs in the
  // AppShell *layout* route, above the /agents/:agentId child match.
  const sessionMatch = useMatch('/agents/:agentId/sessions/:sessionId');
  const agentMatch = useMatch('/agents/:agentId');
  const routeAgentId = sessionMatch?.params.agentId ?? agentMatch?.params.agentId ?? null;
  const routeSessionId = sessionMatch?.params.sessionId ?? null;

  const { agents, agentsLoading, agentsError } = useAgentCatalog();
  const { selectThread } = useChatWorkspaceController();

  const sessionEpoch = useChatViewStore((state) => state.sessionEpoch);
  const selectedAgentId = useChatViewStore((state) => state.selectedAgentId);
  const resolvedThreadId = useChatViewStore((state) =>
    state.selectedAgentId ? state.agentThreads[state.selectedAgentId] ?? null : null,
  );
  // A thread accepted for this tab's current turn but not yet selected into
  // agentThreads (selection is deferred until the run finishes, so assistant-ui
  // doesn't remount mid-stream — see ChatWorkspace.handleThreadAccepted). Used
  // only to address the URL; never substituted for resolvedThreadId anywhere
  // that drives rendering/selection, so it can't affect remount behavior.
  const pendingAcceptedThreadId = useChatViewStore((state) =>
    state.selectedAgentId ? state.pendingAcceptedThreadIds[state.selectedAgentId] ?? null : null,
  );

  // store → URL
  useEffect(() => {
    if (!enabled || !selectedAgentId || !isChatPath(pathname)) return;
    const addressableThreadId = resolvedThreadId ?? pendingAcceptedThreadId;
    const target = addressableThreadId
      ? `/agents/${selectedAgentId}/sessions/${addressableThreadId}`
      : `/agents/${selectedAgentId}`;
    if (target === pathname) return;
    // Replace (don't push) when only refining the same agent's URL with its
    // resolved session, so Back doesn't land on the agent-only entry.
    const replace = !!addressableThreadId && pathname === `/agents/${selectedAgentId}`;
    navigate(target, { replace });
  }, [enabled, selectedAgentId, resolvedThreadId, pendingAcceptedThreadId, pathname, navigate]);

  // URL → store (one-shot). Capture the initial route once; the mirror above may
  // change the live URL before this settles.
  const initial = useRef({ agentId: routeAgentId, sessionId: routeSessionId });
  const applied = useRef(false);
  useEffect(() => {
    if (!enabled || applied.current) return;
    if (sessionEpoch === 0) return; // wait for ChatSessionProvider to hydrate
    const { agentId, sessionId } = initial.current;
    if (!agentId) {
      applied.current = true;
      return;
    }
    if (agentsLoading) return; // decide once the catalog can confirm access
    applied.current = true;
    // Unknown/inaccessible agent → home. A session the user does not own is left
    // to the existing thread-fetch 404 path, not pre-validated here.
    if (!agentsError && !agents.some((a) => a.id === agentId)) {
      navigate('/', { replace: true });
      return;
    }
    // A null thread is a view-store-only write (see controller.selectThread):
    // it lands this tab on a new chat without creating a thread server-side
    // or clearing the workspace's active thread for other tabs.
    selectThread(agentId, sessionId);
  }, [enabled, sessionEpoch, agents, agentsLoading, agentsError, selectThread, navigate]);
}
